import type { GetServerSidePropsContext, NextPage } from 'next';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import tracking, { TrackingContentGroup } from 'src/tracking';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Recipe as RecipeSchema, WebPage as WebPageSchema } from 'schema-dts';
import { jsonLdScriptProps } from 'react-schemaorg';
import { isMaintenance, set503Header, setDefaultCacheHeader, setNoCacheHeader, ssrPersistedQuery, transformSeoToMetas } from '../../helpers/SSRHelpers';
import { initWithLangAndLabels } from '../../i18n';
import RecipeTemplate, { RecipeTemplateProps } from '../../templates/RecipeTemplate';
import { Article, Format, PageTemplate, Recipe } from '../../graphql/generated/api-graphql';
import { Icons } from '../../components/atoms/Icon';
import { RecipeMainContentProps } from '../../components/molecules/RecipeMainContent';
import RecipeNutritionFacts, { RecipeNutritionFactsProps } from '../../components/molecules/RecipeNutritionFacts';
import BrandPush from '../../components/molecules/BrandPush';
import BMIPush from '../../components/molecules/BMIPush';
import toBMIPushProps from '../../relay/BMIPush/transform';
import { jsonLdRecipe, toRecipeNutritionFactsTable, toScoreLetter } from '../../relay/Common/recipe';
import BrandPushTransform from '../../relay/BrandPush/transform';
import RecipeHeroTransform from '../../relay/RecipeHero/transform';
import RecipeHeroDetailsTransform from '../../relay/RecipeHeroDetails/transform';
import { wediaImagesTransform } from '../../relay/Common/wedia';
import { app } from '../../configuration';
import { transformRecipeCard } from '../../relay/RecipeCard/transformRecipeCard';
import Router, { routes } from '../../routes/Router';
import recipePage from '../../graphql/Services/Recipe/queries/recipePage';
import { transformArticleCard } from '../../relay/ArticleCard/transformArticleCard';
import { ProductPopinProps } from '../../components/molecules/ProductPopin';
import productPopinTransform from '../../relay/ProductPopin/transform';
import { transformTagForCloud } from '../../relay/Common/tag';
import { Configuration } from '../../graphql/Services';
import { TagsCloudProps } from '../../components/molecules/TagsCloud';
import { CardsBlockProps } from '../../components/molecules/CardsBlock';
import { usePageActions } from '../../relay/Common/usePageActions';
import { GetServerSidePropsReturnType, PageProps } from '../../relay/Common/PageProps';
import format from '../../graphql/Services/Cheese/queries/format';
import { SearchType } from '../../relay/SearchEngine/searchType';
import BugsnagHelpers from '../../helpers/BugsnagHelpers';
import { adPropsTransformer } from '../../relay/Common/adPropsTransformer';
import { jsonLdWebPage } from '../../relay/Common/page';
import BreadcrumbWrapper from '../../relay/Breadcrumb/BreadcrumbWrapper';
import HeaderWrapper from '../../relay/Header/HeaderWrapper';
import FooterWrapper from '../../relay/Footer/FooterWrapper';
type RecipePageProps = PageProps & {
  data: Recipe;
  articles: Article[];
  popinProps?: Format;
};
const RecipePage: NextPage<RecipePageProps> = ({
  data,
  articles,
  configuration
}) => {
  const {
    t
  } = useTranslation();
  const router = useRouter();
  useEffect(() => {
    tracking.page({
      brand: undefined,
      group: TrackingContentGroup.RECIPE,
      page: data?.title,
      section: undefined,
      hideRubrique: true
    });
  }, [data?.title]);
  const [popinData, setPopinData] = useState<ProductPopinProps | undefined>(undefined);
  const [openOpin, setOpenPopin] = useState(false);
  // const [loadedFromSSRData, setLoadedFromSSRData] = useState(false)

  const handleClose = useCallback(() => {
    setOpenPopin(false);
    router.push(Router.getRouteUrl(routes.recipe, {
      slug: data?.slug
    }), undefined, {
      shallow: true
    });
  }, [data?.slug, router]);
  const handleLoadOpenPopin = useCallback((product: Format) => {
    const transformed = productPopinTransform({
      t,
      data: {
        format: product
      }
    });
    setPopinData(transformed);
    setOpenPopin(true);
  }, [t]);
  const breadcrumbEntries = useMemo(() => [{
    label: configuration?.pagesByTemplate?.[PageTemplate.HpRecipes]?.title,
    href: configuration?.pagesByTemplate?.[PageTemplate.HpRecipes]?.slug
  }, {
    label: data?.title
  }], [data?.title, configuration]);
  const adProps = adPropsTransformer(data?.brand ?? undefined, configuration, true);

  // ------
  // main

  const videoSources = data?.video?.sources?.filter(item => item?.source).filter(Boolean);
  const actionsProps = usePageActions({
    id: data.id,
    text: data?.title,
    url: app.APP_URL + Router.getRouteUrl(routes.recipe, {
      slug: data?.slug
    }),
    brand: data?.brand?.name
  });
  const mainContentProps: RecipeMainContentProps = {
    intro: data?.introduction ? {
      text: data?.introduction
    } : undefined,
    videoTitle: data?.video?.alt ?? data?.video?.title ?? undefined,
    videoProps: videoSources && videoSources.length > 0 ? {
      sources: videoSources.map(item => ({
        src: item.source ?? '',
        type: item.mimeType ?? ''
      })),
      controls: true,
      jsonLdBasePath: app.APP_URL
    } : undefined,
    actions: actionsProps ?? undefined,
    ...(data.ingredients && {
      ingredientsProps: {
        title: t('the_ingredients'),
        nbOfPeople: {
          text: `${data.servings} ${t('servings')}`,
          icon: {
            icon: Icons.convive
          }
        },
        ingredients: data.ingredients.map(ingredient => ({
          amount: ingredient.quantity,
          name: ingredient.text,
          product: ingredient.product ? {
            name: ingredient.product.title,
            linkProps: {
              href: ingredient.product?.slug ? Router.getRouteUrl(routes.format, {
                slug: ingredient.product?.slug
              }) : undefined,
              onClick: e => {
                e.preventDefault();
                if (!ingredient.product) return;
                handleLoadOpenPopin(ingredient.product);
                router.push(Router.getRouteUrl(routes.recipe, {
                  slug: data?.slug,
                  format: ingredient.product?.slug
                }), undefined, {
                  shallow: true
                });
              }
            },
            image: {
              width: 70,
              height: 70,
              maxWidth: 70,
              withHD: true,
              images: ingredient.product.thumbnail ? wediaImagesTransform(ingredient.product.thumbnail) : undefined
            }
          } : undefined
        }))
      }
    }),
    ...(data.directions && {
      stepsProps: {
        title: t('the_preparation'),
        steps: data.directions.map((step, index) => ({
          index: `${index + 1}`,
          htmlProps: {
            text: step
          }
        }))
      }
    }),
    ...(data?.outroTitle || data?.outroBody ? {
      text: {
        text: `
              ${data.outroTitle ? `<h2>${data.outroTitle}</h2>` : ''} 
              ${data.outroBody}
            `
      }
    } : {})
  };

  // ------
  // popin

  const productPopinProps: ProductPopinProps | undefined = useMemo(() => {
    return popinData ? {
      ...popinData,
      popin: {
        ...popinData.popin,
        isOpen: openOpin,
        closeHandler: handleClose
      }
    } : undefined;
  }, [handleClose, openOpin, popinData]);

  // ------
  // sidebar

  const recipeNutritionFactsProps: RecipeNutritionFactsProps | undefined = data?.nutritionPortionBlock || data?.nutrition100gBlock ? {
    title: data?.tipsNutritionTitle ?? '',
    score: {
      value: toScoreLetter(data.nutriscoreLetterV2 ?? data.nutriscoreLetter),
      isV2: data.nutriscoreLetterV2 !== undefined
    },
    tables: data?.nutritionPortionBlock || data?.nutrition100gBlock ? [...toRecipeNutritionFactsTable(t, data?.nutritionPortionBlock), ...toRecipeNutritionFactsTable(t, data?.nutrition100gBlock)] : undefined
  } : undefined;
  const withBMI = data.tags.map(datum => datum.id).filter(value => app.HEALTH_TAG_IDS.includes(value)).length > 0;

  // ------
  // related

  const themeRelatedRecipesProps: RecipeTemplateProps['themeRelatedRecipesProps'] = data.thematicRelatedRecipes && data.thematicRelatedRecipes.length > 0 ? {
    title: t('recipes_same_theme'),
    cards: data.thematicRelatedRecipes.map(recipe => transformRecipeCard(t, recipe, t('recipes_same_theme'))),
    actionButtonProps: {
      label: t('browse_recipes'),
      iconPosition: 'right',
      iconProps: {
        icon: Icons.arrowRight
      },
      href: configuration?.pagesByTemplate?.[PageTemplate.HpRecipes]?.slug,
      onClick: () => tracking.cta(t('browse_recipes'), t('recipes_same_theme'))
    }
  } : undefined;
  const brandRelatedRecipesProps: RecipeTemplateProps['brandRelatedRecipesProps'] = data.brandOtherRecipes && data.brandOtherRecipes.length > 0 ? {
    title: t('recipes_same_brand'),
    cards: data.brandOtherRecipes.map(recipe => transformRecipeCard(t, recipe, t('recipes_same_brand'))),
    actionButtonProps: data.brand?.slug ? {
      label: t('browse_recipes_brand'),
      iconPosition: 'right',
      iconProps: {
        icon: Icons.arrowRight
      },
      href: Router.getRouteUrl(routes.searchType, {
        type: SearchType.Recipe,
        marques: data.brand.id
      }),
      onClick: () => tracking.cta(t('browse_recipes_brand'), t('recipes_same_brand'))
    } : undefined
  } : undefined;
  const articlesProps: CardsBlockProps | undefined = articles && articles.length > 0 ? {
    title: t('lets_talk_about_cheese'),
    cards: articles.map(article => transformArticleCard(t, article, t('lets_talk_about_cheese'))),
    actionButtonProps: {
      label: t('more_articles'),
      iconPosition: 'right',
      iconProps: {
        icon: Icons.arrowRight
      },
      href: Router.getRouteUrl(routes.articles),
      onClick: () => tracking.cta(t('more_articles'), t('lets_talk_about_cheese'))
    }
  } : undefined;
  const tagsCloudProps: TagsCloudProps = {
    title: t('more_cheese'),
    tags: [...(data?.positiveFood ? [{
      label: t('positive_food'),
      href: Router.getRouteUrl(routes.tag.name, {
        slug: 'positivefood'
      }),
      onClick: () => tracking.tag(t('positive_food'), data.title ?? '')
    }] : []), ...(data.tags && data.tags.length > 0 ? data.tags.map(tag => transformTagForCloud(tag, data.title)) : [])]
  };
  const webPageJsonLd = jsonLdWebPage(data.title, data?.introduction ?? '', data?.mainImage?.variations?.[1]?.url ?? data?.mainImage?.variations?.[0]?.url ?? data?.mainImage?.downloadUrl ?? '');
  return <>
      <Head>
        {jsonLdRecipe(data, t) && <script {...jsonLdScriptProps<RecipeSchema>({
        '@context': 'https://schema.org',
        ...jsonLdRecipe(data, t)
      })} />}
        {webPageJsonLd && <script {...jsonLdScriptProps<WebPageSchema>({
        '@context': 'https://schema.org',
        ...webPageJsonLd
      })} />}
      </Head>
      <RecipeTemplate idContent="Content" header={<HeaderWrapper data={configuration?.header} socials={configuration?.footer?.socialLinks} />} footer={<FooterWrapper data={configuration?.footer} />} breadcrumb={<BreadcrumbWrapper entries={breadcrumbEntries} />} adProps={adProps} heroProps={RecipeHeroTransform(t, data)} heroDetailsProps={RecipeHeroDetailsTransform(t, data)} mainContentProps={mainContentProps} sideContent={<>
            {data.brand && <BrandPush {...BrandPushTransform({
        t: t,
        brand: data.brand,
        formats: data.formats ? [...data.formats].slice(0, 2) : undefined,
        handlePopin: product => {
          handleLoadOpenPopin(product);
          router.push(Router.getRouteUrl(routes.recipe, {
            slug: data?.slug,
            format: product?.slug
          }), undefined, {
            shallow: true
          });
        }
      })} />}
            {recipeNutritionFactsProps && <RecipeNutritionFacts {...recipeNutritionFactsProps} />}
            {withBMI && <BMIPush {...toBMIPushProps(t)} />}
          </>} themeRelatedRecipesProps={themeRelatedRecipesProps} brandRelatedRecipesProps={brandRelatedRecipesProps} tagsCloudProps={tagsCloudProps} articlesProps={articlesProps} productPopinProps={productPopinProps} />
    </>;
};
export default RecipePage;
export async function getServerSideProps(context: GetServerSidePropsContext): GetServerSidePropsReturnType<RecipePageProps> {
  try {
    const formatSlug = context?.query?.format;
    const [rs, configurationRs, formatRs] = await Promise.all([ssrPersistedQuery(recipePage, {
      slug: context?.query?.slug
    }), ssrPersistedQuery(Configuration.queries.configuration), ...(formatSlug ? [ssrPersistedQuery(format, {
      slug: formatSlug
    })] : [])]);
    const {
      recipe: data,
      articles
    } = rs?.data ?? {};
    const configuration = configurationRs?.data ?? null;
    const i18n = initWithLangAndLabels('fr', {});
    if (!configuration || rs?.errors) {
      set503Header(context);
      return {
        props: {
          i18n,
          error: true
        }
      };
    }
    if (isMaintenance(configuration)) {
      set503Header(context);
    } else {
      setDefaultCacheHeader(context);
    }
    if (!data) {
      return {
        notFound: true,
        props: {
          i18n,
          configuration
        }
      };
    }
    return {
      props: {
        i18n,
        configuration,
        metas: transformSeoToMetas(Router.getRouteUrl(routes.recipe, {
          slug: context?.query?.slug
        }), data?.seo),
        data,
        articles: articles ?? null,
        ...(formatRs?.data && {
          popinProps: formatRs?.data
        })
      }
    };
  } catch (e: any) {
    BugsnagHelpers?.notify(e, event => {
      const {
        resolvedUrl,
        query,
        params
      } = context;
      event.addMetadata('context', {
        resolvedUrl,
        query,
        params
      });
    });
    setNoCacheHeader(context);
    return {
      notFound: true
    };
  }
}